/**
 * adoArticles.js — Contenido completo de la Serie ADO
 * Arquitectura de Decisión Organizacional · 7 artículos
 * David Prudencio · Edutech Pulse Lab · Mayo–Julio 2026
 */

export const adoArticles = [
  /* ───────────────────────────────────────────────────────── ADO·00 */
  {
    slug: 'ado-00-la-innovacion-necesita-un-lugar',
    numero: '00',
    headerImage: '/images/ado/Headers/cabecera-ADO-00.jpg',
    titulo: 'La innovación necesita un lugar para sobrevivir',
    deck: 'Las organizaciones suelen tener zonas claras para operar, medir y ejecutar. Pero muy pocas tienen un espacio real para proteger aquello que todavía no puede demostrar resultados inmediatos.',
    date: '11 mayo 2026',
    readTime: '8 min',
    stage: { num: '01', label: 'EL FENÓMENO' },
    sections: [
      {
        type: 'lede',
        content: 'Hay una pregunta que pocas organizaciones se hacen con honestidad: ¿dónde vive la innovación dentro de nuestra estructura? No en el discurso estratégico. No en los valores corporativos. No en la presentación anual donde se declara que la innovación es una prioridad. La pregunta real es más incómoda: ¿quién decide qué iniciativas sobreviven? ¿Qué recursos se protegen cuando la operación presiona? ¿Qué espacio tiene una idea nueva antes de ser absorbida por las urgencias del día a día? Ese es el punto de partida de esta serie.',
      },
      {
        type: 'body',
        content: 'Muchas organizaciones no fracasan en innovación porque les falten ideas, talento o intención. Fracasan porque intentan alojar iniciativas exploratorias dentro de estructuras diseñadas para operar con eficiencia, control y predictibilidad. El problema no es que la organización rechace explícitamente la innovación. El problema es que no le da un lugar donde pueda sobrevivir.',
      },
      { type: 'h2', content: 'Las cuatro zonas de Moore' },
      {
        type: 'body',
        content: 'Geoffrey Moore, en Zone to Win, propone un mapa útil para entender esta tensión. Su aporte más importante para esta discusión no es solo estratégico, sino organizacional: muestra que una empresa establecida no opera bajo una sola lógica.',
      },
      {
        type: 'body',
        content: 'La zona de rendimiento es el motor de ingresos. Concentra la mayor parte de la atención directiva y opera bajo una lógica de resultados visibles: compromisos semanales, revisiones mensuales, presión trimestral. Su función es proteger el negocio actual.',
      },
      {
        type: 'body',
        content: 'La zona de productividad sostiene la operación. Ahí viven los servicios compartidos, los procesos, la infraestructura y los sistemas que permiten que el negocio funcione con eficiencia. Su métrica principal no es explorar, sino optimizar.',
      },
      {
        type: 'body',
        content: 'Estas dos zonas son necesarias. Sin ellas, la organización pierde estabilidad. Pero ambas trabajan bajo una lógica de corto plazo. Buscan control, eficiencia, continuidad y reducción de incertidumbre. La tensión aparece cuando una organización intenta hacer innovación desde esas mismas zonas.',
      },
      {
        type: 'body',
        content: 'La zona de incubación, según Moore, opera con otra lógica. Ahí deberían vivir las iniciativas que todavía no son el negocio actual, pero que podrían convertirse en una fuente futura de crecimiento. Requieren recursos dedicados, gobernanza diferenciada, métricas distintas y un horizonte más largo. No pueden ser evaluadas como si ya fueran operación madura.',
      },
      {
        type: 'body',
        content: 'La zona de transformación aparece en momentos excepcionales, cuando la organización necesita mover una iniciativa estratégica de alto impacto. Es una zona de alto riesgo, alta prioridad y baja frecuencia.',
      },
      {
        type: 'image',
        path: '/images/ado/articulos/ado_00_grafico_1.webp',
        caption: 'Figura 1. El vacío estructural de la innovación: la zona de incubación suele ser la menos protegida.',
        source: 'Adaptado de Moore, G. A. (2015). Zone to Win. Diversion Books.',
      },
      { type: 'h2', content: 'Lo que el mapa revela' },
      {
        type: 'body',
        content: 'El mapa es útil porque revela algo que muchas organizaciones prefieren no mirar: las prácticas de gestión que generan éxito en una zona pueden destruir lo que intenta crecer en otra.',
      },
      {
        type: 'body',
        content: 'Lo que funciona para sostener la operación —control, predictibilidad, eficiencia, presión por resultados inmediatos— puede asfixiar una iniciativa que necesita exploración, aprendizaje y tolerancia a la ambigüedad. No porque las personas sean incompetentes. No porque los equipos no tengan compromiso. No porque la metodología esté mal aplicada. Sino porque la iniciativa está viviendo en el lugar equivocado.',
      },
      { type: 'h2', content: 'El problema que esta serie busca explorar' },
      {
        type: 'body',
        content: 'Muchas organizaciones dicen que quieren innovar, pero no han construido una zona real para incubar innovación. Tienen proyectos especiales. Tienen encargos estratégicos. Tienen pilotos. Tienen comités. Tienen presentaciones. Pero no siempre tienen una arquitectura que proteja esas iniciativas cuando la operación vuelve a exigir atención, capacidad y presupuesto.',
      },
      {
        type: 'body',
        content: 'Y cuando eso ocurre, la innovación no necesita ser cancelada. Simplemente empieza a perder oxígeno. Primero se reduce la dedicación. Luego se posterga una decisión. Después se reasigna una persona clave. Más adelante se cambia la prioridad. Finalmente, la iniciativa sigue existiendo en nombre, pero ya no tiene fuerza real. Nadie la mató. La organización solo dejó de protegerla.',
      },
      {
        type: 'body',
        content: 'Ese es el tipo de fracaso que suele pasar desapercibido. No aparece como una decisión formal. No se registra como cancelación. No genera una alerta visible. Pero ocurre todos los días.',
      },
      {
        type: 'pullquote',
        content: 'Cuando una organización intenta innovar sin haber construido un espacio estructural para hacerlo, no está cometiendo solo un error de ejecución. Está creando una contradicción sistémica.',
      },
      {
        type: 'body',
        content: 'Les pide a sus equipos que exploren, pero los mide como si estuvieran ejecutando. Les pide que aprendan, pero les exige certeza. Les pide que imaginen el futuro, pero les retira recursos cuando el presente presiona.',
      },
      {
        type: 'body',
        content: 'Eso no es un problema de creatividad. No es solo un problema de cultura. No es únicamente un problema de metodología. Es un problema de arquitectura de decisión organizacional.',
      },
      {
        type: 'body',
        content: 'La pregunta, entonces, no es si la organización quiere innovar. La pregunta es más exigente: ¿tiene un lugar real donde la innovación pueda sobrevivir antes de demostrar valor?',
      },
      {
        type: 'body',
        content: 'Ese será el foco de esta serie. ADO — Arquitectura de Decisión Organizacional — parte de una idea simple: las iniciativas no sobreviven por intención declarada. Sobreviven cuando existen condiciones organizacionales que las protegen del sistema que, de forma natural, intenta devolver todo al presente.',
      },
      {
        type: 'body',
        content: 'El siguiente artículo examina los modelos que explican cómo nace la innovación y la pregunta crítica que muchos de ellos no responden.',
      },
      {
        type: 'reference',
        label: 'Nota de referencia',
        text: 'Este artículo toma como punto de partida el modelo de zonas propuesto por Geoffrey A. Moore en Zone to Win para desarrollar una lectura propia desde la Arquitectura de Decisión Organizacional.',
        citation: 'Moore, G. A. (2015). Zone to Win. Diversion Books.',
      },
    ],
  },

  /* ───────────────────────────────────────────────────────── ADO·01 */
  {
    slug: 'ado-01-la-innovacion-no-tiene-quien-la-sostenga',
    numero: '01',
    headerImage: '/images/ado/Headers/cabecera-ADO-01.jpg',
    titulo: 'La innovación no tiene quién la sostenga',
    deck: 'Los frameworks explican cómo una idea se convierte en valor. ADO observa por qué ese proceso se rompe cuando entra en la organización real.',
    date: '15 mayo 2026',
    readTime: '9 min',
    stage: { num: '01', label: 'EL FENÓMENO' },
    sections: [
      {
        type: 'lede',
        content: 'En ADO·00 planteé que la innovación necesita un lugar estructural para sobrevivir. Este segundo artículo mira un problema relacionado: muchas organizaciones entienden el ciclo de innovación, pero no tienen las condiciones de decisión necesarias para mantener ese ciclo conectado cuando aparece la presión operativa.',
      },
      {
        type: 'body',
        content: 'Existe una literatura sólida sobre cómo se forma la innovación. Describe etapas, actores, retroalimentaciones y el movimiento que lleva una idea hasta convertirse en valor. Ese trabajo importa porque evita un error frecuente: tratar la innovación como un evento aislado.',
      },
      {
        type: 'body',
        content: 'Pero dentro de una organización establecida, entender el ciclo no es suficiente. La pregunta difícil no es solo cómo emerge la innovación. La pregunta difícil es quién mantiene vivo ese ciclo cuando la organización empieza a separarlo.',
      },
      { type: 'h2', content: 'Lo que los frameworks explican bien' },
      {
        type: 'body',
        content: 'Una forma útil de entender la innovación es distinguir cuatro conceptos que muchas veces se usan como sinónimos: creatividad, invención, innovación y emprendimiento. Están conectados, pero no significan lo mismo.',
      },
      {
        type: 'body',
        content: 'La creatividad es la generación de ideas originales. Nace de la curiosidad, la observación, la imaginación y la capacidad de mirar un problema de otra manera. Pero la creatividad, por sí sola, no produce valor organizacional. Es el punto de ignición, no el sistema que convierte la novedad en impacto.',
      },
      {
        type: 'body',
        content: 'La invención convierte una idea en algo concreto: un prototipo, una solución técnica, un mecanismo o un artefacto nuevo. Algo original que antes no existía. Pero una invención todavía no es innovación. Puede ser técnicamente nueva y, aun así, no tener mercado, adopción ni relevancia estratégica para la organización.',
      },
      {
        type: 'body',
        content: 'La innovación aparece cuando esa novedad se transforma en valor práctico. No es la mera existencia de algo nuevo. Es el proceso mediante el cual esa novedad se vuelve aplicable, usable, adoptable y valiosa en un contexto específico.',
      },
      {
        type: 'body',
        content: 'El emprendimiento conecta esa innovación con el mundo real. Moviliza recursos, asume riesgos, toma decisiones bajo incertidumbre y empuja la idea hacia un entorno donde debe producir resultados.',
      },
      {
        type: 'body',
        content: 'Esta distinción importa porque muestra que la innovación no es un paso único. Es un sistema de interacción. La creatividad puede activar una invención. La invención puede alimentar la innovación. La innovación puede generar actividad emprendedora. Y las señales del mercado pueden regresar al ciclo para reformular la idea original.',
      },
      {
        type: 'image',
        path: '/images/ado/articulos/ado_01_grafico_1.webp',
        caption: 'Figura 1. El ciclo de creatividad, invención, innovación y emprendimiento muestra cómo nace el valor, pero no quién lo sostiene dentro de una organización.',
        source: 'Adaptado de Thomassen, Løje y Basaiawmoit (2018).',
      },
      {
        type: 'body',
        content: 'El ciclo aprende mientras avanza. Eso es lo que muchas organizaciones no ven cuando hablan de "gestión de la innovación". Se enfocan en las etapas, pero el valor real no está en las etapas por separado. El valor aparece cuando las dimensiones correctas se mantienen conectadas durante el tiempo suficiente para producir algo relevante.',
      },
      { type: 'h2', content: 'Dónde aparece realmente el valor' },
      {
        type: 'body',
        content: 'La pregunta importante no es si una organización tiene creatividad, invención, innovación o energía emprendedora. Muchas organizaciones tienen todo eso en alguna parte. El problema es que suelen existir en lugares distintos, bajo incentivos distintos, con responsables distintos y horizontes de tiempo distintos.',
      },
      {
        type: 'body',
        content: 'La creatividad puede vivir en talleres. La invención puede vivir en prototipos. La innovación puede vivir en un roadmap. El emprendimiento puede aparecer en discursos sobre crecimiento, mercado o transformación. Pero el valor no aparece porque esos elementos existan por separado. El valor aparece cuando permanecen conectados.',
      },
      {
        type: 'image',
        path: '/images/ado/articulos/ado_01_grafico_2.webp',
        caption: 'Figura 2. El valor aparece en la intersección activa entre novedad, aplicabilidad y ejecución; el desafío organizacional es mantener esa intersección viva.',
        source: 'Adaptado de Thomassen, Løje y Basaiawmoit (2018).',
      },
      {
        type: 'body',
        content: 'Una organización puede tener personas creativas, prototipos, programas de innovación y lenguaje emprendedor, y aun así fallar en la creación de valor. El problema no es la ausencia de componentes. El problema es la incapacidad de mantenerlos conectados bajo presión.',
      },
      {
        type: 'body',
        content: 'Esta es la parte incómoda: el valor no es resultado de ejecutar cada componente de forma independiente. Es resultado de sostener su interacción durante el tiempo suficiente para que la novedad se convierta en algo útil, adoptado y estratégicamente relevante. Y eso no ocurre solo.',
      },
      { type: 'h2', content: 'La pregunta que el modelo no responde' },
      {
        type: 'body',
        content: 'Muchos frameworks describen el ciclo de innovación como si la organización tuviera una capacidad natural para mantenerlo en movimiento. Esa suposición puede funcionar mejor en contextos emprendedores, donde la supervivencia fuerza la integración.',
      },
      {
        type: 'body',
        content: 'En una startup, la idea, el prototipo, la señal del mercado y la ejecución no pueden permanecer desconectados demasiado tiempo. Si se desconectan, la empresa muere. La supervivencia se convierte en un mecanismo brutal, pero efectivo, de alineamiento.',
      },
      {
        type: 'body',
        content: 'En una organización grande, ese mecanismo no existe por defecto. El negocio actual ya funciona. Ya tiene clientes, ingresos, métricas, responsables, comités, presupuestos, incentivos y presión operativa. Tiene legitimidad porque produce resultados visibles hoy. Eso significa que la innovación no solo intenta avanzar. Compite internamente contra un sistema que ya sabe cómo ganar atención.',
      },
      {
        type: 'body',
        content: 'Ahí aparece la pregunta que falta: ¿quién tiene la autoridad, los recursos y el mandato real para mantener conectado el ciclo de innovación cuando la operación diaria presiona en sentido contrario? Esa pregunta no es secundaria. Es central.',
      },
      {
        type: 'body',
        content: 'Porque lo que ocurre en la práctica no es que las organizaciones no entiendan el ciclo de innovación. La falla es más sutil: sus sistemas de decisión separan los elementos que deberían mantenerse conectados. La idea se discute en una sala. El prototipo se construye en otra. El caso de negocio se revisa en otro comité. La señal del mercado la interpreta otra área. La decisión de continuar, pausar, financiar, escalar o matar la iniciativa queda influida por personas que no necesariamente comparten la misma comprensión del problema.',
      },
      {
        type: 'body',
        content: 'El ciclo existe en teoría. Pero en la práctica, la organización lo fragmenta.',
      },
      { type: 'h2', content: 'La falla no es metodológica' },
      {
        type: 'body',
        content: 'Aquí es donde muchas organizaciones leen mal el problema. Cuando la innovación no avanza, la respuesta instintiva suele ser agregar más método: más design thinking, más ceremonias ágiles, más comités de gobierno, más revisiones de portafolio, más dashboards, más talleres. Esas herramientas pueden ayudar. Pero no resuelven por sí solas el problema central.',
      },
      {
        type: 'body',
        content: 'Una metodología puede ordenar el trabajo. No puede proteger atención por sí sola. No puede garantizar recursos. No puede resolver trade-offs estratégicos. No puede darle autoridad real a una iniciativa. No puede impedir que el negocio operativo absorba personas, presupuesto y capacidad de decisión cuando aumenta la presión.',
      },
      {
        type: 'body',
        content: 'Por eso una iniciativa de innovación puede verse metodológicamente correcta y, aun así, disolverse organizacionalmente. La organización puede entender el ciclo. Puede incluso tener un lenguaje sofisticado para describirlo. Pero si no protege las condiciones que permiten que ese ciclo continúe, la iniciativa queda expuesta. Y las iniciativas expuestas rara vez sobreviven a la presión operativa.',
      },
      {
        type: 'body',
        content: 'Se retrasan. Se reformulan. Se absorben dentro de prioridades existentes. Pierden su ambición original. Siguen visibles en presentaciones, pero desaparecen de la agenda real de decisión. Nadie necesita rechazarlas. El sistema solo necesita dejar de protegerlas.',
      },
      { type: 'h2', content: 'Qué agrega ADO' },
      {
        type: 'body',
        content: 'Aquí es donde la Arquitectura de Decisión Organizacional agrega una capa distinta. ADO no es otro modelo para explicar cómo se genera innovación. Es una forma de examinar si la organización tiene las condiciones de decisión necesarias para que la innovación sobreviva después de haber sido declarada importante.',
      },
      {
        type: 'body',
        content: 'La pregunta no es solo si la organización tiene ideas. No es solo si tiene frameworks, programas de innovación o equipos entrenados. La pregunta es si tiene mecanismos que respondan cuatro asuntos prácticos: quién decide, qué recursos se protegen, dónde vive la iniciativa, y durante cuánto tiempo la organización está dispuesta a sostenerla antes de que la presión operativa reinicie la prioridad.',
      },
      {
        type: 'body',
        content: 'Sin esas respuestas, la innovación sigue siendo vulnerable. Puede empezar con creatividad. Puede producir invención. Puede formularse como innovación. Puede tener energía emprendedora detrás. Pero si la organización no protege la intersección donde se crea valor, la iniciativa eventualmente será fragmentada. No porque el framework sea incorrecto. Sino porque la organización no tiene una arquitectura capaz de sostener aquello que el framework describe.',
      },
      { type: 'h2', content: 'El segundo principio' },
      {
        type: 'body',
        content: 'El segundo principio de la Arquitectura de Decisión Organizacional es este: la innovación no necesita solo un ciclo. Necesita un sistema capaz de mantener ese ciclo conectado bajo presión. Eso es lo que muchos frameworks dejan sin resolver.',
      },
      {
        type: 'body',
        content: 'Por eso muchas organizaciones no fallan antes de que la innovación empiece. Fallan después. Fallan cuando el ciclo necesita continuidad, pero la organización solo entrega atención episódica. Fallan cuando el valor requiere interacción, pero la organización separa la propiedad. Fallan cuando la exploración requiere protección, pero el sistema recompensa la urgencia operativa.',
      },
      {
        type: 'pullquote',
        content: 'La mayoría de las organizaciones tiene a alguien responsable de lanzar innovación. Muchas menos tienen a alguien responsable de mantenerla viva.',
      },
      {
        type: 'body',
        content: 'Eso no es un descuido menor. Es una decisión estructural, incluso cuando nadie la toma explícitamente. Porque aquello que el liderazgo no decide por diseño, la lógica operativa de la organización lo decide por defecto.',
      },
      {
        type: 'body',
        content: 'En ADO 00, la pregunta fue dónde vive la innovación. En ADO 01, la pregunta es quién protege el ciclo durante el tiempo suficiente para que pueda convertirse en valor. Ahí es donde muchas organizaciones todavía no miran.',
      },
      {
        type: 'body',
        content: 'El siguiente artículo examina una forma silenciosa de fracaso organizacional: ADO 02 — La iniciativa no fue cancelada. Simplemente dejó de existir.',
      },
      {
        type: 'reference',
        label: 'Nota de referencia',
        text: 'Este artículo toma como punto de partida el modelo de creatividad, invención, innovación y emprendimiento desarrollado por Thomassen, Løje y Basaiawmoit para desarrollar una lectura propia desde la Arquitectura de Decisión Organizacional.',
        citation: 'Thomassen, J. M., Løje, H. y Basaiawmoit, R. V. (2018). Creativity, Invention, Innovation and Entrepreneurship.',
      },
    ],
  },

  /* ───────────────────────────────────────────────────────── ADO·02 */
  {
    slug: 'ado-02-la-iniciativa-dejo-de-existir',
    numero: '02',
    headerImage: '/images/ado/Headers/cabecera-ADO-02.jpg',
    titulo: 'La iniciativa no fue cancelada. Simplemente dejó de existir',
    deck: 'Muchas iniciativas estratégicas no fracasan por una mala decisión. Se diluyen cuando la operación captura a las personas, los recursos y la atención que debían sostenerlas.',
    date: '21 mayo 2026',
    readTime: '8 min',
    stage: { num: '02', label: 'CÓMO OCURRE' },
    sections: [
      {
        type: 'lede',
        content: 'Muchas iniciativas estratégicas no mueren por una decisión formal. Mueren cuando dejan de tener a alguien sosteniéndolas. No hay comité de cancelación. No hay correo final. No hay una decisión explícita. Solo ocurre algo más difícil de ver: la operación vuelve a capturar a las personas, los recursos y la atención que la iniciativa necesitaba para seguir existiendo.',
      },
      {
        type: 'body',
        content: 'Alguien en tu organización está intentando convertir una oportunidad difusa en algo ejecutable. No trabaja solo. Articula personas de distintas áreas que aportan conocimiento parcial, criterio técnico y contexto operativo. Pero nadie está dedicado por completo. Todos siguen teniendo responsabilidades del día a día. Todos siguen siendo medidos por aquello que ocurre hoy, no por aquello que podría transformar el mañana.',
      },
      {
        type: 'body',
        content: 'Lo que están construyendo juntos no es solo una herramienta, un proceso o un sistema. Es una capacidad interna: la posibilidad de que la organización aprenda a responder a un cambio antes de que ese cambio la obligue a reaccionar.',
      },
      {
        type: 'body',
        content: 'Entonces llega algo urgente. Una decisión que destrabar. Una reunión crítica. Una incidencia que atender. Un comité que preparar. Una presión del negocio que no puede esperar.',
      },
      {
        type: 'body',
        content: 'Lo que estaban construyendo no tiene una fecha límite clara ni una consecuencia visible si se detiene hoy. Lo urgente sí. La persona que articulaba la iniciativa cierra lo que estaba revisando y abre lo otro. Los demás hacen exactamente lo mismo, cada uno desde su propia agenda.',
      },
      {
        type: 'body',
        content: 'Nadie decide cancelar la iniciativa. Simplemente empieza a diluirse. Y con ella se diluye algo más importante: la posibilidad de salir del mismo loop operativo en que la organización lleva años funcionando bien, sin notar que funcionar bien dejó de ser suficiente.',
      },
      {
        type: 'body',
        content: 'Eso no es descuido. Es el sistema produciendo exactamente los resultados para los que fue optimizado.',
      },
      { type: 'h2', content: 'El momento que nadie registra' },
      {
        type: 'body',
        content: 'La persona que articulaba esa iniciativa no es cualquiera. Es lo que llamo un perfil explorador: alguien capaz de convertir ambigüedad en ejecución. No se trata de ejecutar un resultado ya definido. Se trata de descubrir qué debe crearse mientras se demuestra que vale la pena crearlo. En ese tipo de iniciativa, el problema todavía no está completamente formulado, la dirección no está cerrada y el camino no puede reducirse a una lista estable de entregables.',
      },
      {
        type: 'body',
        content: 'Ahí está la diferencia crítica. Un proyecto operativo puede avanzar con planificación, responsables y control de avance. Una iniciativa exploratoria necesita algo más frágil: continuidad de razonamiento, conexión entre áreas, legitimidad informal y suficiente espacio para que una idea todavía inmadura se convierta en algo defendible. Eso es precisamente lo que la operación absorbe.',
      },
      {
        type: 'body',
        content: 'No es una única urgencia lo que rompe semanas de trabajo. Es la presión del día a día funcionando como modo habitual. Cuando los temas urgentes ocupan el centro de la agenda, lo que se pierde no son solo horas. Se pierde la continuidad de una conversación que necesita sostenerse en el tiempo. Se pierde la conexión con un equipo que solo se mantiene activo si alguien lo articula. Se pierde la posición que la iniciativa tenía en la conversación organizacional.',
      },
      {
        type: 'body',
        content: 'Para cuando la presión cede, ya no hay desde dónde retomar. No porque alguien haya tomado una mala decisión. Al contrario: hizo lo que el sistema premia. Respondió al lugar donde había presión, visibilidad y consecuencia. La iniciativa, en cambio, no tenía todavía una arquitectura que la defendiera. Ahí empieza la disolución: no como evento, sino como acumulación.',
      },
      { type: 'h2', content: 'Por qué los recursos no vuelven' },
      {
        type: 'body',
        content: 'Cuando la urgencia se resuelve, cuando una reunión crítica pasa, cuando la presión cede, la iniciativa ya no tiene el mismo lugar. No porque alguien la haya cancelado. Sino porque perdió momentum institucional: aquello que sostiene a una iniciativa entre una conversación y la siguiente.',
      },
      {
        type: 'body',
        content: 'La operación no registra esa pérdida. Registra decisiones tomadas, incidentes atendidos, comités realizados, pendientes resueltos, indicadores cumplidos y compromisos cerrados. Pero no siempre registra que una iniciativa estratégica dejó de tener cadencia, que sus conversaciones se enfriaron, que sus datos quedaron desactualizados o que su narrativa perdió presencia en la agenda.',
      },
      {
        type: 'pullquote',
        content: 'Sin cadencia de revisión, no hay datos. Sin datos, no hay conversación. Sin conversación, la iniciativa desaparece de la agenda. Y cuando desaparece de la agenda, los recursos que eventualmente regresan encuentran otra urgencia esperando.',
      },
      {
        type: 'body',
        content: 'La pérdida de visibilidad no es consecuencia del fracaso de la iniciativa. Es el mecanismo que lo produce.',
      },
      {
        type: 'image',
        path: '/images/ado/articulos/ado_02_grafico_1.webp',
        caption: 'Figura 1. La absorción operativa no cancela la iniciativa; captura progresivamente los recursos, la atención y el perfil explorador que la sostienen.',
        source: 'Elaboración propia.',
      },
      { type: 'h2', content: 'Lo que revela el mapa de absorción' },
      {
        type: 'body',
        content: 'El mapa muestra cómo la presión operativa actúa como fuerza de absorción. No necesita oponerse explícitamente a la iniciativa. Basta con capturar, una y otra vez, los recursos y el perfil explorador que la sostienen.',
      },
      {
        type: 'body',
        content: 'Cuando los recursos se dispersan, el seguimiento activo se debilita. Cuando el perfil explorador es capturado, el momentum cae. Y cuando seguimiento y momentum desaparecen, la iniciativa entra en disolución silenciosa.',
      },
      {
        type: 'body',
        content: 'La operación no rechaza la innovación. La absorbe. Y lo hace cada vez que algo urgente compite con algo estratégico sin que exista una arquitectura que proteja lo segundo.',
      },
      {
        type: 'body',
        content: 'El mismo sistema que necesita ese perfil para innovar lo captura sistemáticamente para operar. Una organización diseñada para reducir la ambigüedad difícilmente protege a quienes deben habitarla para crear algo nuevo, a menos que haya decidido explícitamente construir ese espacio.',
      },
      { type: 'h2', content: 'Lo que el sistema produce sin que nadie lo decida' },
      {
        type: 'body',
        content: 'El resultado no es simplemente el fracaso de una iniciativa. Es algo más costoso: la organización empieza a comportarse como si anunciar una prioridad fuera lo mismo que construir las condiciones para sostenerla. Aprende que el anuncio reemplaza a la arquitectura. Que la intención reemplaza al mecanismo. Que basta con decir que algo importa, aunque nadie haya protegido el tiempo, los recursos, la cadencia y el perfil que podrían sostenerlo.',
      },
      {
        type: 'body',
        content: 'Y así se reinicia el ciclo con la siguiente iniciativa declarada. Con el mismo entusiasmo. Con los mismos recursos parcialmente disponibles. Con el mismo perfil explorador asignado de manera informal. Con la misma presión del día a día esperando absorberlo todo cuando vuelva a aparecer una urgencia.',
      },
      {
        type: 'body',
        content: 'Si en tu organización ese perfil siempre termina resolviendo lo urgente, el problema no está en el perfil. Está en la arquitectura que lo vuelve inevitable.',
      },
      {
        type: 'body',
        content: 'En ADO 01, la pregunta fue quién protege el ciclo de innovación durante el tiempo suficiente para que pueda convertirse en valor. ADO 02 muestra lo que ocurre cuando nadie lo hace.',
      },
      {
        type: 'body',
        content: 'La iniciativa puede tener sentido estratégico. Puede tener talento alrededor. Puede tener respaldo inicial. Puede tener una narrativa convincente. Pero si depende de atención residual, recursos prestados y articulación informal, no está protegida. Está expuesta.',
      },
      {
        type: 'body',
        content: 'Y una iniciativa expuesta no necesita ser cancelada para desaparecer. Solo necesita competir, una vez más, contra una operación que ya sabe cómo ganar.',
      },
      {
        type: 'body',
        content: 'El siguiente artículo introduce las cuatro dimensiones del problema: lo que debe existir simultáneamente para que una iniciativa tenga probabilidad real de sobrevivir. ADO 03 — Fue declarada prioridad estratégica. Y aun así no sobrevivió.',
      },
      {
        type: 'reference',
        label: 'Nota de referencia',
        text: 'Este artículo toma como punto de partida una observación práctica sobre la absorción operativa de iniciativas exploratorias. Esa lectura tiene resonancia con lo que Joseph Bower documentó en Managing the Resource Allocation Process: el proceso real de asignación de recursos en las organizaciones suele operar desde niveles medios con lógica operativa, no únicamente desde la intención estratégica declarada por la alta dirección.',
        citation: 'Bower, J. L. (1970). Managing the Resource Allocation Process. Harvard Business School Press.',
      },
    ],
  },

  /* ───────────────────────────────────────────────────────── ADO·03 */
  {
    slug: 'ado-03-prioridad-estrategica-no-fue-suficiente',
    numero: '03',
    headerImage: '/images/ado/Headers/cabecera-ADO-03.jpg',
    titulo: 'Fue declarada prioridad estratégica, y aun así no sobrevivió',
    deck: 'Las cuatro condiciones que deben existir simultáneamente para que una iniciativa tenga probabilidad real de avanzar — más allá de la declaración.',
    date: '27 mayo 2026',
    readTime: '10 min',
    stage: { num: '02', label: 'CÓMO OCURRE' },
    sections: [
      {
        type: 'lede',
        content: 'Hay un momento que casi ningún directivo recuerda con precisión. No es el momento en que se anuncia la iniciativa. No es el momento en que se forma el equipo. Es el momento en que alguien, en alguna reunión de agenda apretada, pregunta cómo va esa iniciativa que lanzaron hace cuatro meses — y nadie en la sala puede responder con certeza.',
      },
      {
        type: 'body',
        content: 'No porque la iniciativa haya fracasado. Sino porque ya no tiene un lugar claro donde vivir.',
      },
      {
        type: 'body',
        content: 'Ese momento no es un accidente. Es el resultado predecible de haber declarado una prioridad sin construir las condiciones que la sostienen.',
      },
      {
        type: 'body',
        content: 'En ADO 02 describí cómo la operación absorbe las iniciativas exploratorias sin que nadie tome una decisión de cancelarlas. Lo que no describí es qué necesitaría haber existido para que eso no ocurriera. Eso es lo que este artículo introduce.',
      },
      { type: 'h2', content: 'Las cuatro dimensiones no son un checklist' },
      {
        type: 'body',
        content: 'Antes de nombrarlas, es necesario decir lo que no son. No son etapas de un proceso. No son áreas de responsabilidad que se pueden distribuir entre departamentos. No son buenas prácticas que se implementan de manera gradual.',
      },
      {
        type: 'body',
        content: 'Son condiciones que deben existir simultáneamente. La ausencia de cualquiera de ellas es suficiente para producir lo que en esta serie llamo disolución silenciosa — incluso si las otras tres están completamente activas. Una iniciativa con gobernanza clara, recursos protegidos y persistencia estratégica, pero sin espacio estructural propio, termina siendo absorbida por la lógica operativa de quien la alberga. Una iniciativa con todo lo anterior, pero sin gobernanza real, queda a merced del criterio informal del siguiente gerente que tenga algo más urgente que atender.',
      },
      {
        type: 'body',
        content: 'No necesitan fallar las cuatro. La ausencia de una es suficiente para colapsar el conjunto.',
      },
      {
        type: 'image',
        path: '/images/ado/articulos/ado_03_grafico_1.webp',
        caption: 'La iniciativa no se sostiene si falta alguna de los 4 pilares. Las cuatro condiciones simultáneas: Dónde vive (Espacio estructural), Quién decide (Autoridad de decisión), Qué se protege (Recursos blindados), Cuánto dura (Persistencia).',
        source: 'Elaboración propia.',
      },
      { type: 'h2', content: 'Primera dimensión: dónde vive' },
      {
        type: 'body',
        content: 'Una iniciativa exploratoria necesita habitar la ambigüedad. Necesita operar con métricas distintas a las de la operación, con ritmos de decisión distintos, con tolerancia a la incertidumbre que la operación no puede permitirse. Necesita, en términos concretos, un espacio estructural que le permita existir sin ser traducida constantemente al lenguaje del negocio que ya funciona.',
      },
      {
        type: 'body',
        content: 'El problema no es que la operación sea hostil a la innovación. Es que está diseñada para reducir la ambigüedad, controlar la varianza y proteger lo que ya produce resultados. Eso es exactamente lo correcto para operar, y exactamente lo incorrecto para explorar.',
      },
      {
        type: 'body',
        content: 'Una iniciativa que convive con estructuras diseñadas para que nada cambie demasiado rápido no tiene espacio para convertirse en algo nuevo. Tiene espacio para volverse una versión más familiar de lo que ya existe.',
      },
      { type: 'h2', content: 'Segunda dimensión: quién decide' },
      {
        type: 'body',
        content: 'Toda iniciativa exploratoria enfrenta, tarde o temprano, decisiones que no están en el manual. Decisiones sobre si continuar cuando los primeros resultados son ambiguos. Sobre cómo priorizar cuando el equipo tiene que elegir entre dos direcciones igualmente razonables. Sobre qué pasa cuando la iniciativa empieza a incomodar a una unidad de negocio que no estaba en el diseño original.',
      },
      {
        type: 'body',
        content: 'Esas decisiones necesitan a alguien con mandato claro y autoridad verificable. No un patrocinador entusiasta. No un comité que se reúne trimestralmente. Alguien que tenga poder real sobre qué avanza y qué no, y que sea reconocido como tal por el resto de la organización.',
      },
      {
        type: 'body',
        content: 'Sin eso, las decisiones se toman por inercia, por presión de quien grita más fuerte, o simplemente no se toman. Y una iniciativa que no puede decidir en los momentos que importan no puede avanzar en ningún momento.',
      },
      { type: 'h2', content: 'Tercera dimensión: qué se protege' },
      {
        type: 'body',
        content: 'Los recursos de las iniciativas exploratorias son siempre los primeros en redistribuirse cuando el negocio central presiona. No porque alguien quiera sabotear la iniciativa, sino porque la lógica operativa premia lo que tiene consecuencias visibles e inmediatas. La innovación, por definición, no las tiene todavía.',
      },
      {
        type: 'body',
        content: 'Una iniciativa exploratoria necesita presupuesto y capacidad que estén blindados de esa dinámica. No asignados en papel, sino protegidos en la práctica: que cuando llegue la próxima crisis operativa — y llegará — esos recursos no sean los primeros candidatos a la redistribución.',
      },
      {
        type: 'body',
        content: 'La diferencia entre recursos declarados y recursos protegidos es exactamente la diferencia entre una iniciativa que existe en el organigrama y una que tiene probabilidad real de producir algo.',
      },
      { type: 'h2', content: 'Cuarta dimensión: cuánto dura' },
      {
        type: 'body',
        content: 'Cuánto dura una iniciativa depende directamente de cuánto dura su prioridad. Y la prioridad declarada es la dimensión más frágil de todas.',
      },
      {
        type: 'body',
        content: 'Las organizaciones no cambian sus prioridades por decisión maliciosa. Las cambian porque cada crisis operativa genera presión real, visible e inmediata, y porque las iniciativas exploratorias no tienen todavía la masa crítica para competir con esa presión en condiciones iguales.',
      },
      {
        type: 'body',
        content: 'Sin un mecanismo explícito que garantice que la prioridad no rota cuando cambia el contexto, cada crisis reinicia el reloj. Y lo que se pierde no es solo el tiempo — es el momentum institucional que describí en ADO 02: la cadencia de revisión, la conexión entre áreas, la narrativa activa que mantiene a la iniciativa en la agenda de quienes tienen que sostenerla.',
      },
      {
        type: 'body',
        content: 'Una iniciativa que sobrevive solo mientras las prioridades no cambien, no está protegida. Está esperando a que algo urgente la desplace.',
      },
      { type: 'h2', content: 'Lo que el diagrama muestra' },
      {
        type: 'body',
        content: 'Las cuatro dimensiones no operan en secuencia. Operan como estructura de soporte: cada una sostiene a la iniciativa desde un ángulo distinto, y la iniciativa tiene probabilidad real de sobrevivir solo cuando las cuatro están activas al mismo tiempo. La presión operativa no necesita atacar las cuatro. Le basta con erosionar una.',
      },
      {
        type: 'body',
        content: 'La mayoría de las organizaciones que reconocen este problema llegan a la misma conclusión: necesitamos más recursos dedicados, un equipo más comprometido, un liderazgo más visible. Esa intuición es comprensible — y es exactamente el lugar donde el argumento se complica. Eso es lo que el siguiente artículo examina.',
      },
      {
        type: 'reference',
        label: 'Nota de referencia',
        text: 'Lo que aquí describo tiene resonancia con lo que James March documentó en 1991: la exploración y la explotación compiten por los mismos recursos de atención, tiempo y capital — y sin condiciones explícitas que protejan la exploración, la explotación gana sistemáticamente.',
        citation: 'March, J. G. (1991). Exploration and Exploitation in Organizational Learning. Organization Science, 2(1), 71–87.',
      },
    ],
  },

  /* ───────────────────────────────────────────────────────── ADO·04 */
  {
    slug: 'ado-04-el-sistema-la-volvio-inviable',
    numero: '04',
    headerImage: '/images/ado/Headers/cabecera-ADO-04.jpg',
    titulo: 'La iniciativa tenía datos. El sistema pedía otros',
    deck: 'Cuando el sistema de evaluación está calibrado para el negocio que ya existe, la exploración pierde antes de que nadie tome una decisión en su contra.',
    date: '19 junio 2026',
    readTime: '9 min',
    stage: { num: '03', label: 'POR QUÉ LAS SOLUCIONES HABITUALES NO FUNCIONAN' },
    sections: [
      { type: 'h2', content: 'El momento en que el sistema gana sin pelear' },
      {
        type: 'body',
        content: 'Hay un tipo de reunión que muchos directivos conocen bien, aunque no siempre puedan describirla con precisión.',
      },
      {
        type: 'lede',
        content: 'Una iniciativa estratégica lleva meses en desarrollo. Tiene un equipo. Tiene un nombre. Tiene una narrativa sólida. Cuando llega el momento de presentarla formalmente, alguien en la sala hace la pregunta que parece razonable: ¿cuál es el retorno esperado? ¿En qué plazo? ¿Cómo se compara con otras inversiones que ya están produciendo resultados?',
      },
      {
        type: 'body',
        content: 'La iniciativa tiene datos. Pero no los que el sistema reconoce como suficientes. No porque sea una mala iniciativa — sino porque es exploratoria, y las preguntas fueron diseñadas para evaluar algo completamente distinto.',
      },
      {
        type: 'body',
        content: 'Nadie vota en contra. Nadie la cancela. El comité pide más información, más evidencia, más certeza antes de comprometer recursos. La iniciativa queda en espera de una revisión que nunca tiene la urgencia suficiente para ocurrir.',
      },
      {
        type: 'body',
        content: 'Eso no es una decisión maliciosa. Es el sistema funcionando exactamente como fue diseñado.',
      },
      { type: 'h2', content: 'El filtro que nadie construyó para explorar' },
      {
        type: 'body',
        content: 'Clayton Christensen documentó en 1997 un patrón que sigue siendo incómodo para muchas organizaciones: las empresas no fracasan en innovación por irracionalidad. Fracasan por racionalidad perfecta aplicada al contexto equivocado.',
      },
      {
        type: 'body',
        content: 'Los sistemas de decisión organizacionales se calibran con el tiempo para servir a los clientes actuales, optimizar las métricas actuales y proteger los márgenes actuales. Eso es correcto. Es lo que hace que una organización funcione bien.',
      },
      {
        type: 'body',
        content: 'El problema aparece cuando esa misma lógica se aplica a evaluar iniciativas que, por definición, no pueden demostrar todavía lo que el sistema exige. Una iniciativa exploratoria no tiene clientes establecidos. No tiene márgenes probados. No tiene un historial de resultados que justifique la inversión en los términos que el sistema reconoce como válidos.',
      },
      {
        type: 'body',
        content: 'El filtro no es hostil. Es simplemente incompatible. El sistema no es irracional: es perfectamente racional para el core actual. Y esa racionalidad es exactamente lo que elimina la exploración antes de que pueda demostrar valor.',
      },
      {
        type: 'image',
        path: '/images/ado/articulos/ado_04_grafico_1.webp',
        caption: 'El ciclo de incompatibilidad estructural: señales débiles → evaluación con métricas del core → solicitud de más evidencia → la iniciativa no puede madurar.',
        source: 'Elaboración propia.',
      },
      { type: 'h2', content: 'Lo que el mapa revela' },
      {
        type: 'body',
        content: 'Una iniciativa exploratoria entra al sistema de evaluación con señales débiles: hipótesis en lugar de datos, aprendizajes en lugar de resultados, potencial en lugar de certeza. El sistema, calibrado para señales fuertes del negocio actual, las interpreta correctamente como insuficientes. Pide más evidencia. La iniciativa necesita más tiempo y recursos para producir esa evidencia. El sistema no los libera sin evidencia previa. La iniciativa no puede madurar dentro de esa lógica.',
      },
      {
        type: 'body',
        content: 'No es un ciclo de mala fe. Es un ciclo de incompatibilidad estructural.',
      },
      { type: 'h2', content: 'Por qué no se arregla con mejor argumentación' },
      {
        type: 'body',
        content: 'La respuesta instintiva cuando una iniciativa no pasa el filtro de evaluación suele ser mejorar la presentación: más datos, mejor narrativa, un caso de negocio más sólido.',
      },
      {
        type: 'body',
        content: 'Esa respuesta no resuelve el problema. Lo desplaza.',
      },
      {
        type: 'body',
        content: 'El problema no es la calidad de la argumentación. Es que la argumentación ocurre dentro de un sistema de evaluación que no tiene categorías para lo que la iniciativa es todavía. Una iniciativa exploratoria presentada con métricas de negocio maduro no se vuelve más defendible — se vuelve más fácil de rechazar, porque ahora el sistema puede medir con precisión cuánto le falta para justificar la inversión.',
      },
      {
        type: 'body',
        content: 'El ajuste que se necesita no es en la presentación. Es en la lógica con que se evalúa. Y esa lógica no cambia sola.',
      },
      { type: 'h2', content: 'La pregunta que sigue' },
      {
        type: 'body',
        content: 'Si el problema no es cómo se defiende la iniciativa, sino desde dónde se la juzga, la pregunta siguiente es directa: ¿quién tiene autoridad para protegerla de ese filtro mientras madura?',
      },
      {
        type: 'body',
        content: 'La respuesta más frecuente es también la más frágil.',
      },
      {
        type: 'reference',
        label: 'Nota de referencia',
        text: "Lo que aquí describo tiene resonancia con lo que Clayton Christensen documentó en The Innovator's Dilemma: las organizaciones exitosas fracasan en innovación disruptiva no por incompetencia, sino porque sus sistemas de decisión están perfectamente calibrados para sostener el negocio actual.",
        citation: "Christensen, C. M. (1997). The Innovator's Dilemma. Harvard Business School Press.",
      },
    ],
  },

  /* ───────────────────────────────────────────────────────── ADO·05 */
  {
    slug: 'ado-05-cuando-depende-de-una-persona',
    numero: '05',
    headerImage: '/images/ado/Headers/cabecera-ADO-05.jpg',
    titulo: 'Cuando una iniciativa depende de una persona para sobrevivir',
    deck: 'El patrocinio informal puede proteger una iniciativa por un tiempo. No reemplaza una arquitectura.',
    date: '26 junio 2026',
    readTime: '11 min',
    stage: { num: '03', label: 'POR QUÉ LAS SOLUCIONES HABITUALES NO FUNCIONAN' },
    sections: [
      { type: 'h2', content: 'Lo que ocurre cuando el filtro se supera' },
      {
        type: 'lede',
        content: 'Supongamos que la iniciativa sobrevivió la evaluación. Encontró a alguien con suficiente autoridad y suficiente convicción para defenderla dentro del sistema. Consiguió recursos. Consiguió tiempo. Consiguió un espacio en la agenda.',
      },
      {
        type: 'body',
        content: 'Ahora depende de esa persona para seguir existiendo.',
      },
      {
        type: 'body',
        content: 'Cada semana, en alguna reunión donde la operación compite por atención y presupuesto, alguien tiene que recordar que esta iniciativa existe, que importa, que merece seguir recibiendo lo que necesita. Si esa persona no está en la sala, la iniciativa no tiene voz. Si tiene algo más urgente que atender, la iniciativa espera. Si cambia de rol, se va de la organización o decide que el costo político de seguir defendiéndola supera el beneficio visible, la iniciativa pierde su único mecanismo de supervivencia.',
      },
      {
        type: 'body',
        content: 'No porque haya fallado. Sino porque nunca tuvo un mecanismo institucional que la sostuviera.',
      },
      { type: 'h2', content: 'El patrón que Burgelman formalizó' },
      {
        type: 'body',
        content: 'Robert Burgelman documentó este mecanismo en 1983 al estudiar cómo las iniciativas estratégicas internas realmente avanzan o mueren dentro de las organizaciones. Su hallazgo fue incómodo: la supervivencia de una iniciativa depende, en gran medida, del patrocinio activo de un gerente de nivel medio con suficiente disposición política para sostenerla ante la presión operativa.',
      },
      {
        type: 'body',
        content: 'Sin ese patrocinio, la iniciativa no llega a la agenda directiva. Con ese patrocinio, puede avanzar — mientras el patrocinador permanezca, mantenga su capital político y decida invertirlo en esta iniciativa por sobre otras que también compiten por su atención.',
      },
      {
        type: 'body',
        content: 'Lo que Burgelman observó no fue una falla individual. Fue un patrón sistémico: las organizaciones delegan en personas lo que deberían institucionalizar en mecanismos.',
      },
      {
        type: 'pullquote',
        content: 'Cuando una iniciativa necesita ser defendida por una persona cada semana, el problema no es la fortaleza del sponsor. Es la ausencia de un mecanismo que la proteja sin depender de él.',
      },
      { type: 'h2', content: 'Las tres fragilidades simultáneas' },
      {
        type: 'body',
        content: 'Una iniciativa sin mecanismo institucional queda expuesta a tres formas de fragilidad al mismo tiempo.',
      },
      {
        type: 'body',
        content: 'La primera es fragilidad política: el patrocinador tiene su propio capital limitado y otras iniciativas que también requieren su defensa. En algún momento, el costo de sostener esta iniciativa supera lo que puede justificar.',
      },
      {
        type: 'body',
        content: 'La segunda es fragilidad de permanencia: los roles cambian, las reorganizaciones ocurren, las prioridades personales evolucionan. El patrocinador de hoy no es una garantía para mañana.',
      },
      {
        type: 'body',
        content: 'La tercera es fragilidad de atención: cada crisis operativa es una competencia directa por el tiempo y el foco del patrocinador. Y la operación siempre tiene más urgencia visible que una iniciativa exploratoria.',
      },
      {
        type: 'body',
        content: 'Las tres operan simultáneamente. Y ninguna depende de la voluntad del patrocinador — dependen de la estructura que lo rodea.',
      },
      {
        type: 'image',
        path: '/images/ado/articulos/ado_05_grafico_1.webp',
        caption: 'Las tres fragilidades del patrocinio informal: política, permanencia y atención. Ninguna depende de la voluntad del patrocinador — dependen de la estructura que lo rodea.',
        source: 'Elaboración propia.',
      },
      { type: 'h2', content: 'Por qué no se arregla con mejor liderazgo' },
      {
        type: 'body',
        content: 'La respuesta instintiva cuando el patrocinio falla suele ser buscar a alguien más comprometido, con más autoridad, con más visibilidad ejecutiva.',
      },
      {
        type: 'body',
        content: 'El patrocinio informal puede proteger una iniciativa por un tiempo. Pero no reemplaza una arquitectura.',
      },
      {
        type: 'body',
        content: 'Un sponsor más poderoso es un mecanismo más robusto, pero sigue siendo un mecanismo con fecha de expiración. Cuando esa persona cede, se va o cambia de agenda, la iniciativa vuelve a quedar expuesta — esta vez sin el momentum que había acumulado.',
      },
      {
        type: 'body',
        content: 'El patrón se repite con cada generación de sponsors. Porque el problema nunca fue el sponsor. Fue la ausencia de algo que no dependa de ninguno en particular.',
      },
      { type: 'h2', content: 'La pregunta que sigue' },
      {
        type: 'body',
        content: 'Si la iniciativa no puede quedar capturada por una métrica incompatible, ni por una persona específica, la pregunta que queda no es operativa.',
      },
      {
        type: 'body',
        content: 'Es estructural.',
      },
      {
        type: 'body',
        content: '¿Qué tendría que existir para que la iniciativa no quede capturada por una métrica incompatible ni por una persona específica — y tenga probabilidad real de sobrevivir?',
      },
      {
        type: 'body',
        content: 'Eso no tiene respuesta en la literatura de liderazgo. Tiene respuesta en la arquitectura.',
      },
      {
        type: 'reference',
        label: 'Nota de referencia',
        text: 'Lo que aquí describo tiene resonancia con lo que Robert Burgelman documentó en Corporate Entrepreneurship and Strategic Management: la supervivencia de las iniciativas estratégicas internas depende del patrocinio activo de niveles medios — un mecanismo informal que la organización raramente institucionaliza.',
        citation: 'Burgelman, R. A. (1983). Corporate Entrepreneurship and Strategic Management: Insights from a Process Study. Management Science, 29(12), 1349–1364.',
      },
    ],
  },

  /* ───────────────────────────────────────────────────────── ADO·06 */
  {
    slug: 'ado-06-arquitectura-para-sobrevivir',
    numero: '06',
    headerImage: '/images/ado/Headers/cabecera-ADO-06.jpg',
    titulo: 'Una iniciativa puede estar en el roadmap y no tener arquitectura para sobrevivir',
    deck: 'ADO no promete que toda innovación sobreviva. Hace visible algo más incómodo: cuándo una organización nunca le dio condiciones reales para existir.',
    date: '3 julio 2026',
    readTime: '12 min',
    stage: { num: '04', label: 'LA ARQUITECTURA' },
    sections: [
      { type: 'h2', content: 'Lo que la serie mostró' },
      {
        type: 'lede',
        content: 'La serie empezó con una pregunta simple: ¿dónde vive la innovación dentro de la estructura?',
      },
      {
        type: 'body',
        content: 'Terminó mostrando algo más incómodo: muchas iniciativas no mueren por falta de ideas ni de intención. Mueren porque el sistema las evalúa con la lógica equivocada, las sostiene con el mecanismo equivocado y las declara prioridad sin construir las condiciones que las protegerían.',
      },
      {
        type: 'body',
        content: 'La pregunta que sigue no es cómo mejorar esos mecanismos. Es qué necesita existir para que la iniciativa no dependa de ellos para madurar.',
      },
      { type: 'h2', content: "Lo que Tushman y O'Reilly dejaron abierto" },
      {
        type: 'body',
        content: "Tushman y O'Reilly propusieron en 1996 que las organizaciones que innovan de forma sostenida son ambidiestras: diferencian estructuralmente las unidades de exploración y explotación, con liderazgo, métricas y lógicas distintas para cada una, integradas bajo un liderazgo senior capaz de sostener ambas tensiones.",
      },
      {
        type: 'body',
        content: 'Ese aporte sigue siendo válido. ADO parte de él y baja la tensión a una pregunta más específica: qué arquitectura de decisión necesita una iniciativa exploratoria para sobrevivir cuando compite con el core — no solo en la estructura, sino en la práctica diaria de asignación de recursos, autoridad y tiempo.',
      },
      {
        type: 'body',
        content: 'La separación estructural es necesaria. No es suficiente. Los laboratorios de innovación mueren dentro de estructuras duales porque la separación física no cambia las cuatro condiciones operativas que determinan si una iniciativa tiene probabilidad real de existir.',
      },
      {
        type: 'body',
        content: 'Las cuatro dimensiones no son recomendaciones de gestión. Son condiciones mínimas simultáneas. Sin ellas, el método, el sponsor y la intención operan sobre una base rota.',
      },
      { type: 'h2', content: 'Las cuatro compuertas' },
      {
        type: 'body',
        content: 'Una iniciativa exploratoria necesita pasar cuatro compuertas para tener probabilidad real de existir dentro de una organización que ya tiene un negocio funcionando. La ausencia de cualquiera produce un tipo específico de muerte — no un fallo genérico, sino uno predecible.',
      },
      {
        type: 'body',
        content: 'La primera compuerta es estructural: ¿dónde vive? Si no existe un espacio con lógica propia, la iniciativa es absorbida por la operación. Esa muerte tiene nombre: absorción operativa.',
      },
      {
        type: 'body',
        content: 'La segunda es de autoridad: ¿quién decide cuando hay conflicto con el core? Sin mandato real, las decisiones se toman por inercia o por presión. Esa muerte tiene nombre: inercia por omisión.',
      },
      {
        type: 'body',
        content: 'La tercera es de protección: ¿qué recursos están blindados? Sin protección real, los recursos se redistribuyen cuando la operación presiona. Esa muerte tiene nombre: redistribución silenciosa.',
      },
      {
        type: 'body',
        content: 'La cuarta es de tiempo: ¿con qué métricas y en qué horizonte se evalúa? Sin horizonte de maduración, la iniciativa es juzgada antes de poder demostrar valor. Esa muerte tiene nombre: caducidad prematura.',
      },
      {
        type: 'image',
        path: '/images/ado/articulos/ado_06_grafico_1.webp',
        caption: 'Diagrama de compuertas. Iniciativa exploratoria → Compuerta 1: ¿Dónde vive? → Compuerta 2: ¿Quién decide? → Compuerta 3: ¿Qué se protege? → Compuerta 4: ¿Cuánto dura? → Probabilidad real de sobrevivir.',
        source: 'Elaboración propia.',
      },
      { type: 'h2', content: 'Lo que ADO hace visible' },
      {
        type: 'body',
        content: 'ADO no es un framework de innovación. No reemplaza metodologías de gestión, modelos de portfolio ni estructuras ambidiestras. No promete que toda iniciativa exploratoria sobreviva si las cuatro condiciones existen.',
      },
      {
        type: 'body',
        content: 'Hace visible algo más incómodo: cuándo una organización nunca le dio condiciones reales para existir.',
      },
      {
        type: 'body',
        content: 'La distinción importa. Una iniciativa puede fallar por razones legítimas: el mercado no validó la hipótesis, el timing fue incorrecto, los supuestos no se sostuvieron. Esas son fallas de exploración — y la exploración tiene que poder fallar para ser exploración real.',
      },
      {
        type: 'body',
        content: 'Lo que ADO diagnostica es distinto: cuando una iniciativa muere antes de que ninguna de esas preguntas pueda responderse, porque el sistema la eliminó antes de que tuviera tiempo de madurar.',
      },
      { type: 'h2', content: 'El lenguaje de exclusión' },
      {
        type: 'body',
        content: 'Cuando ADO existe — cuando las cuatro condiciones están activas simultáneamente — ciertas excusas pierden fuerza.',
      },
      {
        type: 'body',
        content: 'El comité de innovación sin autoridad real deja de ser un mecanismo de gobernanza. Pasa a ser lo que siempre fue: una reunión de seguimiento sin poder de decisión.',
      },
      {
        type: 'body',
        content: 'El presupuesto declarado sin protección deja de llamarse inversión en innovación. Pasa a ser lo que siempre fue: recursos disponibles hasta que la operación los necesite.',
      },
      {
        type: 'body',
        content: 'El sponsor sin mandato deja de ser un mecanismo de protección. Pasa a ser lo que siempre fue: un defensor informal con fecha de expiración.',
      },
      {
        type: 'body',
        content: 'La prioridad sin mecanismo deja de ser una declaración estratégica. Pasa a ser lo que siempre fue: una intención que el sistema reemplazará con la próxima urgencia.',
      },
      {
        type: 'body',
        content: 'ADO no agrega una práctica más. Hace que ciertas prácticas ya no puedan presentarse como suficientes.',
      },
      { type: 'h2', content: 'La pregunta final' },
      {
        type: 'body',
        content: 'Antes de declarar que una iniciativa es una prioridad estratégica, hay cuatro preguntas que la organización debería poder responder con precisión.',
      },
      {
        type: 'body',
        content: '¿Dónde va a vivir esta iniciativa dentro de la estructura?',
      },
      {
        type: 'body',
        content: '¿Quién tiene autoridad real sobre qué avanza y qué no cuando compite con la operación?',
      },
      {
        type: 'body',
        content: '¿Qué presupuesto y capacidad están protegidos de la redistribución operativa?',
      },
      {
        type: 'body',
        content: '¿Con qué métricas y en qué horizonte se va a evaluar antes de exigirle resultados de negocio maduro?',
      },
      {
        type: 'body',
        content: 'Si alguna de esas preguntas no tiene respuesta, la iniciativa no está siendo lanzada. Está siendo expuesta.',
      },
      {
        type: 'body',
        content: 'Una iniciativa puede estar en el roadmap y no tener arquitectura para sobrevivir.',
      },
      {
        type: 'reference',
        label: 'Nota de referencia',
        text: "Lo que aquí describo tiene resonancia con lo que Tushman y O'Reilly documentaron en Ambidextrous Organizations: la ambidestreza organizacional requiere no solo separar exploración y explotación, sino integrarlas bajo condiciones de gobernanza, recursos y persistencia que ninguna estructura garantiza por sí sola.",
        citation: "Tushman, M. & O'Reilly, C. (1996). Ambidextrous Organizations: Managing Evolutionary and Revolutionary Change. California Management Review, 38(4), 8–29.",
      },
    ],
  },
];

/* ── Helpers ──────────────────────────────────────────────── */
export const getAdoArticleBySlug = (slug) =>
  adoArticles.find((a) => a.slug === slug) || null;

export const getAdoPrevNext = (slug) => {
  const idx = adoArticles.findIndex((a) => a.slug === slug);
  return {
    prev: idx > 0 ? adoArticles[idx - 1] : null,
    next: idx < adoArticles.length - 1 ? adoArticles[idx + 1] : null,
  };
};
